import { useCallback, useEffect, useState } from 'react'

import { Err, Ok } from 'ts-results'

import { useApplicationStore } from 'app/application/infrastructure'
import { DataError, useApi } from 'app/common/application'

import {
  AvailableConnection,
  AvailableConnectionsGetResponse,
  AvailableOrInstalledConnection,
  ConnectionConfig,
  ConnectionEntity,
  ConnectionName,
  ConnectionsGetResponse,
  InstalledConnection,
  isInstalledConnection,
  mapToAvailableConnections,
  mapToConnections,
} from '../types'
import { toAuthEntity, toCreateEntity, toUpdateEntity } from './mapper'
import useShopifyConnectionApi from './useShopifyConnectionApi'

const useConnections = () => {
  const { create, _delete, read, replace, update } = useApi()
  const { updateShop } = useShopifyConnectionApi()

  const availableConnectionsData = useApplicationStore((state) => state.availableConnections)
  const setAvailableConnectionsData = useApplicationStore((state) => state.setAvailableConnections)
  const [availableConnections, setAvailableConnections] = useState<AvailableConnection[]>(
    mapToAvailableConnections(availableConnectionsData),
  )

  const connectionsData = useApplicationStore((state) => state.connections)
  const addConnectionData = useApplicationStore((state) => state.addConnection)
  const deleteConnectionData = useApplicationStore((state) => state.deleteConnection)
  const setConnectionsData = useApplicationStore((state) => state.setConnections)
  const updateConnectionData = useApplicationStore((state) => state.updateConnection)
  const [connections, setConnections] = useState<InstalledConnection[]>(
    mapToConnections(connectionsData, availableConnections),
  )

  useEffect(
    () =>
      setAvailableConnections((prev) => {
        const updatedConnection = mapToAvailableConnections(availableConnectionsData)
        return JSON.stringify(prev) === JSON.stringify(updatedConnection) ? prev : updatedConnection
      }),
    [availableConnectionsData],
  )

  useEffect(() => {
    if (availableConnections)
      setConnections((prev) => {
        const updatedConnection = mapToConnections(connectionsData, availableConnections)
        return JSON.stringify(prev) === JSON.stringify(updatedConnection) ? prev : updatedConnection
      })
  }, [availableConnections, connectionsData])

  const deleteConnection = useCallback(
    async (connection: InstalledConnection) => {
      const result = await _delete(`/client/v1/connections/${connection.name}`)
      if (result.ok) {
        deleteConnectionData(connection.id)
      }

      return result
    },
    [_delete, deleteConnectionData],
  )
  const fetchConnections = useCallback(async () => {
    const resultAvailableConnections = await read<AvailableConnectionsGetResponse>(
      '/client/v1/connections/available',
    )
    if (resultAvailableConnections.ok) {
      setAvailableConnectionsData(resultAvailableConnections.val.items)
    }

    const resultConnections = await read<ConnectionsGetResponse>('/client/v1/connections')
    if (resultConnections.ok) {
      setConnectionsData(resultConnections.val.items)
    }
  }, [read, setAvailableConnectionsData, setConnectionsData])

  const getConnection = useCallback(
    (name: ConnectionName) =>
      connections.find((connection) => connection.name === name) ??
      availableConnections?.find((availableConn) => availableConn.name === name) ??
      null,

    [availableConnections, connections],
  )

  const saveAuthorization = useCallback(
    async (
      connection: AvailableOrInstalledConnection,
      connectionConfig: ConnectionConfig,
      authorizationCode: string,
      redirectUri: string,
    ) => {
      const request = toAuthEntity(
        { ...connection, config: connectionConfig },
        authorizationCode,
        redirectUri,
      )

      const isUpdate = isInstalledConnection(connection)

      if (request) {
        const result = await (isUpdate ? replace : create)(
          `/client/v1/connections/${connection.name}/oauth`,
          request,
        ).then((result) => (result.ok ? Ok(result.val as ConnectionEntity) : result))

        if (result.ok) {
          if (isUpdate) {
            updateConnectionData(result.val)
          } else {
            addConnectionData(result.val)
          }
        }

        return result
      }

      return Promise.resolve(Err(new DataError(404, 'to be implemented')))
    },
    [addConnectionData, create, replace, updateConnectionData],
  )

  const saveConnection = useCallback(
    async (connection: AvailableOrInstalledConnection, connectionConfig: ConnectionConfig) => {
      const request = isInstalledConnection(connection)
        ? toUpdateEntity({ ...connection, config: connectionConfig })
        : toCreateEntity(connection.name, connectionConfig)

      if (request) {
        const result = await (connection.name === 'shopify'
          ? updateShop((connectionConfig.shop as string).trim())
          : (isInstalledConnection(connection) ? update : create)(
              `/client/v1/connections/${connection.name}`,
              request,
            ).then((result) => (result.ok ? Ok(result.val as ConnectionEntity) : result)))

        if (result.ok) {
          if (isInstalledConnection(connection)) {
            updateConnectionData(result.val)
          } else {
            addConnectionData(result.val)
          }
        }

        return result
      }

      return Promise.resolve(Err(new DataError(404, 'to be implemented')))
    },
    [updateShop, create, update, updateConnectionData, addConnectionData],
  )

  return {
    availableConnections,
    deleteConnection,
    fetchConnections,
    getConnection,
    saveAuthorization,
    saveConnection,
    connections,
  }
}

export default useConnections
